import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { Send } from "lucide-react";
import { Button } from "@ui";
import { type DailyUsageStatus } from "@agentic_conversation/hooks/use-chat-socket";
import { DailyLimitWarning } from "./daily-limit-warning";

interface MessageInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  dailyUsage?: DailyUsageStatus | null;
}

export function MessageInput({ onSend, disabled = false, dailyUsage }: MessageInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Autofocus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!message.trim() || disabled) return;

    onSend(message);
    setMessage("");

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const showWarning = dailyUsage?.is_warning && !dailyUsage?.is_exceeded;

  return (
    <div className="w-full flex relative z-[2]">
      <div className="w-full rounded-lg border border-slate-300 bg-slate-50 p-2 shadow-lg">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex flex-col relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Posez une question..."
              disabled={disabled}
              className="w-full min-h-[64px] max-h-[200px] resize-none rounded-lg border border-slate-300 px-3 py-2 pb-10 text-sm placeholder:text-slate-400 focus:border-default-500 focus:outline-none focus:ring-1 focus:ring-default-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              rows={2}
            />

            {showWarning && (
              <DailyLimitWarning
                remaining={dailyUsage.remaining}
                percentage={dailyUsage.percentage}
                resets_at={dailyUsage.resets_at}
              />
            )}

            <div className="absolute right-4 bottom-2 flex items-center gap-2">
              <Button
                type="submit"
                icon={Send}
                disabled={!message.trim() || disabled}
                size="sm"
              >
                Envoyer
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
